import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, ShoppingCart, X, ZoomIn } from "lucide-react";
import { usePhotosByEvent } from "@/photos/hooks/usePhotos";
import { useLocation } from "wouter";
import { useCartContext } from "@/contexts/CartContext";
import { useUser } from "@/contexts/UserContext";
import type { Photo } from "@/types/schema";
import { WatermarkedImage } from "@/components/ui/watermarked-image";
import { PhotoService } from "@/photos/services/photoService";

interface EventPhotosGridProps {
  eventId: string;
  eventName?: string;
  eventData?: {
    monetizationType?: string;
    pricePerPhoto?: number;
  };
  limit?: number;
  showTitle?: boolean;
  showActions?: boolean;
  className?: string;
}

export default function EventPhotosGrid({
  eventId,
  eventName,
  eventData,
  limit = 12,
  showTitle = true,
  showActions = true,
  className = ""
}: EventPhotosGridProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [, setLocation] = useLocation();

  const { data: photosResponse, isLoading, error } = usePhotosByEvent(eventId, 1, limit);
  const photos = photosResponse?.photos || [];


  const { addToCart, isLoading: cartLoading } = useCartContext();
  const { isLoggedIn } = useUser();

  // Verificar se o evento permite compra de fotos
  // Priorizar dados da foto, depois dados do evento passados como prop
  const canPurchasePhotos = (photos.length > 0 && photos[0]?.event?.monetizationType !== 'free') ||
    (eventData?.monetizationType !== 'free');

  const openLightbox = (photo: Photo) => {
    setSelectedPhoto(photo);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedPhoto(null);
  };


  const handleAddToCart = async (photo: Photo) => {
    if (!isLoggedIn) {
      setLocation('/login');
      return;
    }

    try {
      await addToCart(photo.id, 1);
    } catch (error) {
      console.error('Erro ao adicionar ao carrinho:', error);
    }
  };

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {showTitle && (
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-16" />
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: limit }).map((_, index) => (
            <Skeleton key={index} className="aspect-square rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-2">Erro ao carregar fotos</h3>
        <p className="text-muted-foreground">Tente recarregar a página</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <Camera className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Nenhuma foto disponível</h3>
        <p className="text-muted-foreground">
          {eventName ? `As fotos do evento "${eventName}" ainda não foram publicadas.` : "As fotos deste evento ainda não foram publicadas."}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {showTitle && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Fotos do Evento
              <Badge variant="secondary">{photos.length}</Badge>
            </h3>
            {!canPurchasePhotos && (
              <Badge variant="outline" className="text-green-600 border-green-600">
                Gratuito
              </Badge>
            )}
          </div>
          {photos.length > limit && (
            <Button variant="outline" size="sm">
              Ver Todas ({photosResponse?.total || photos.length})
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo, index) => (
          <Card
            key={photo.id}
            className="group overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300"
            onClick={() => openLightbox(photo)}
          >
            <div className="aspect-square relative overflow-hidden">
              <WatermarkedImage
                src={photo.thumbnailurl || photo.storageurl}
                alt={`Foto ${index + 1} do evento ${eventName || photo.event?.title || ''}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                watermark={PhotoService.shouldWatermark(photo)}
                onError={(e) => {
                  console.error('Erro ao carregar imagem da foto:', photo);
                  e.currentTarget.src = 'https://via.placeholder.com/300x300?text=Imagem+Indisponível';
                }}
              />

              {/* Overlay com ações */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
                  <Button size="sm" variant="secondary" className="h-8 w-8 p-0">
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  {showActions && canPurchasePhotos && (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 p-0"
                      disabled={cartLoading || !isLoggedIn}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToCart(photo);
                      }}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Número da foto */}
              <div className="absolute top-2 left-2">
                <Badge variant="secondary" className="text-xs">
                  #{index + 1}
                </Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Lightbox Modal */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-4xl w-full h-[90vh] p-0 bg-white [&>button]:hidden">
          <DialogHeader className="p-4 pb-0 bg-white border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg">
                Foto do Evento {eventName && `"${eventName}"`}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {showActions && canPurchasePhotos && selectedPhoto && (
                  <Button
                    size="sm"
                    disabled={cartLoading || !isLoggedIn}
                    onClick={() => handleAddToCart(selectedPhoto)}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {isLoggedIn ? 'Comprar' : 'Login para Comprar'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={closeLightbox}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 flex items-center justify-center p-4 pt-0 bg-white">
            {selectedPhoto && (
              <WatermarkedImage
                src={selectedPhoto.storageurl}
                alt={`Foto do evento ${eventName || selectedPhoto.event?.title || ''}`}
                className="max-w-full max-h-full object-contain rounded-lg"
                watermark={PhotoService.shouldWatermark(selectedPhoto)}
                variant="full"
                onError={(e) => {
                  console.error('Erro ao carregar imagem ampliada:', selectedPhoto);
                  e.currentTarget.src = 'https://via.placeholder.com/800x600?text=Imagem+Indisponível';
                }}
              />
            )}
          </div>

          {selectedPhoto && (
            <div className="p-4 pt-0 border-t bg-white">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>Autor: {selectedPhoto.photographer?.name || 'Não informado'}</span>
                <span>Adicionada em: {(() => {
                  try {
                    const dateStr = selectedPhoto.uploadedat || selectedPhoto.uploadedAt;
                    if (!dateStr) return 'Data não disponível';
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) {
                      return 'Data não disponível';
                    }
                    return date.toLocaleDateString('pt-BR');
                  } catch (error) {
                    return 'Data não disponível';
                  }
                })()}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}